'use client';

import React, { FC, useEffect, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import Link from 'next/link';
import BlogCard from '@/components/cards/BlogCard';
import { FormattedBlogPost, formatBlogPost } from '@/lib/blog/blog-utils';
import { FiTerminal, FiArrowRight, FiActivity } from 'react-icons/fi';

interface BlogSectionProps {
  limit?: number;
}

const PAGE_SIZE = 3;

const BlogSection: FC<BlogSectionProps> = ({ limit = PAGE_SIZE }) => {
  const [posts, setPosts] = useState<FormattedBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/blog/posts?page=1&pageSize=${limit}`);
      if (!res.ok) throw new Error('Signal Interrupted');
      
      const data = await res.json();
      
      // Safety check: ensure data.posts exists
      const mapped: FormattedBlogPost[] = (data.posts || []).map((post: any) => 
        formatBlogPost(post)
      );

      setPosts(mapped);
    } catch (err) {
      console.error('Failed to fetch blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [limit]);

  /* ---------------------------------------------
      Framer Motion Variants
  ----------------------------------------------*/
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { staggerChildren: 0.1, delayChildren: 0.1 } 
    },
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } 
    },
  };

  return (
    <section className="w-full">
      {/* Tactical Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 py-10">
          {[...Array(limit)].map((_, i) => (
            <div key={i} className="space-y-4 animate-pulse">
              <div className="aspect-video bg-zinc-100 dark:bg-zinc-900 rounded-3xl" />
              <div className="h-4 bg-zinc-100 dark:bg-zinc-900 rounded w-3/4" />
              <div className="h-4 bg-zinc-100 dark:bg-zinc-900 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : posts.length > 0 ? (
        <>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {posts.map((post) => (
              <motion.div key={post.slug} variants={cardVariants} className="flex">
                <BlogCard post={post} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="flex justify-center"
          >
            <Link
              href="/blog"
              className="group flex items-center gap-3 px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] hover:bg-indigo-600 dark:hover:bg-indigo-500 hover:text-white transition-all duration-300 shadow-xl shadow-indigo-500/10"
            >
              <FiTerminal className="text-indigo-400 group-hover:text-white" />
              Access All Logs
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </>
      ) : (
        /* Empty State */
        <div className="py-20 text-center border-2 border-dashed border-zinc-100 dark:border-zinc-900 rounded-[3rem]">
          <FiActivity className="mx-auto text-zinc-300 dark:text-zinc-800 mb-4 animate-pulse" size={32} />
          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-400">No active transmissions</h3>
        </div>
      )}
    </section>
  );
};

export default BlogSection;